import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSales, getUsers, deleteSale, updateSale, getCustomers, getProducts } from '@/api/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Calendar, User, Package, Trash2, Edit, Download, ShoppingCart, Search, Plus, X } from 'lucide-react';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import * as XLSX from 'xlsx';
import { useActionHistory } from '@/context/ActionHistoryContext';
import PageHeader from '@/components/PageHeader';
import { getLocalDateString, combineDateWithCurrentTime, getCurrentIsoDateTime } from '@/utils/dateUtils';
import { isCameraProduct } from '@/utils/productCategories';

const CATEGORIES = ['Peşin Satış', 'Operatör Satışı', 'Arvento Taksitli', 'Proje Satışı', 'Kamera Ürünleri', 'Sensörler', 'M2M', 'Diğer'];

// ==================== ARVENTO ÜRÜN LİSTESİ (MERKEZİ TANIMLI) ====================
// Bu liste dışındaki ürünler Arvento satış adedine dahil EDİLMEZ
const ARVENTO_PRODUCT_NAMES = [
  "Profesyonel Araç Takip Paketi (Bataryasız)",
  "Profesyonel Araç Takip Paketi (Bataryalı)",
  "Standart Araç Takip Paketi",
  "Standart Araç Takip Paketi Kiralama",
  "Standart Kolay Takip",
  "Standart Araç Takip Paketi (Türk Telekom)",
  "Standart Araç Takip Paketi (Turkcell)",
  "Standart Araç Takip Paketi (Vodafone) 12 AY",
  "Standart Araç Takip Paketi (Vodafone) 24 AY",
  "Profesyonel Araç Takip Paketi Bataryalı (Arvento Taksitli)",
  "Profesyonel Araç Takip Paketi Bataryasız (Arvento Taksitli)",
  "Standart Araç Takip Paketi (Arvento Taksitli)",
  "112 Projesi – Profesyonel Paket",
  "Motat Pro Paket",
  "Motat Projesi Motat SET",
  "Yeşil Takip Projesi Hat Dahil Paket",
  "İmt.14 Motat Proje Satışı",
  "Profesyonel Paket + SIM"
];

// Ürün adının Arvento listesinde olup olmadığını kontrol et
// Fazladan boşlukları normalize ederek karşılaştır
const normalizeProductName = (name) => {
  if (!name) return '';
  // 1. Trim ve lowercase
  let normalized = name.trim().toLowerCase();
  // 2. Parantez etrafındaki boşlukları kaldır: "( " -> "(", " )" -> ")"
  normalized = normalized.replace(/\(\s+/g, '(').replace(/\s+\)/g, ')');
  // 3. Fazladan boşlukları tek boşluğa indir
  normalized = normalized.replace(/\s+/g, ' ');
  return normalized;
};

const isArventoProduct = (productName) => {
  if (!productName) return false;
  const productNameNormalized = normalizeProductName(productName);
  return ARVENTO_PRODUCT_NAMES.some(name => normalizeProductName(name) === productNameNormalized);
};

const Sales = ({ user }) => {
  const navigate = useNavigate();
  const [sales, setSales] = useState([]);
  const [allSales, setAllSales] = useState([]); // Tüm satışları sakla
  const [users, setUsers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [repFilter, setRepFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState(''); // ARAMA FİLTRESİ
  
  // Tarih filtreleri (yerel/TR saatine göre)
  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const [startDate, setStartDate] = useState(getLocalDateString(firstDayOfMonth));
  const [endDate, setEndDate] = useState(getLocalDateString(today));
  
  const { pushAction } = useActionHistory();
  
  // Mobile detection
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 50;
  const [sortConfig, setSortConfig] = useState({ key: 'sale_date', direction: 'desc' });
  // Edit dialog states
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingSale, setEditingSale] = useState(null);
  const [editFormData, setEditFormData] = useState({ 
    product_id: null,
    product_name: '', 
    quantity: 1, 
    camera_quantity: 0,
    price: 0,
    notes: '' 
  });
  const [editUnitPrice, setEditUnitPrice] = useState(0);

  // Create dialog states (YENİ - Çoklu Ürün)
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [customerSearch, setCustomerSearch] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [createFormData, setCreateFormData] = useState({
    customer_id: '',
    notes: '',
    sale_date: getLocalDateString()
  });
  const [saleItems, setSaleItems] = useState([]);
  const [currentItem, setCurrentItem] = useState({
    product_id: '',
    product_name: '',
    category: 'Peşin Satış',
    quantity: 1,
    unit_price: 0,
    discount_percentage: 0
  });

  // Ürün detay modal (YENİ)
  const [showItemsDialog, setShowItemsDialog] = useState(false);
  const [selectedSaleItems, setSelectedSaleItems] = useState([]);

  useEffect(() => {
    if (user?.role === 'admin') {
      loadUsers();
    }
    loadProducts();
    loadCustomers();
  }, [user]);

  // Müşteri dropdown'unu dışarı tıklayınca kapat
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showCustomerDropdown && !event.target.closest('.customer-search-container')) {
        setShowCustomerDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showCustomerDropdown]);

  const loadCustomers = async () => {
    try {
      const data = await getCustomers();
      setCustomers(data);
    } catch (error) {
      console.error('Müşteriler yüklenemedi:', error);
    }
  };

  useEffect(() => {
    loadSales();
    setCurrentPage(1); // Reset page when filters change
  }, [filter, repFilter]);

  useEffect(() => {
    // Kategori filtresi, arama, tarih veya sıralama değiştiğinde satışları filtrele
    filterSales();
  }, [categoryFilter, allSales, sortConfig, searchQuery, startDate, endDate]);

  const loadUsers = async () => {
    try {
      const data = await getUsers();
      setUsers(data);
    } catch (error) {
      console.error('Kullanıcılar yüklenemedi');
    }
  };

  const loadProducts = async () => {
    try {
      const data = await getProducts();
      // Sadece satışlarda gösterilmesi gereken ürünleri filtrele
      const filteredData = data.filter(product => product.for_sales !== false);
      setProducts(filteredData);
    } catch (error) {
      console.error('Ürünler yüklenemedi');
    }
  };

  const loadSales = async () => {
    try {
      const days = filter === 'all' ? null : parseInt(filter);
      
      // Temsilci ve abonelik rolleri sadece kendi satışlarını görsün
      let rep;
      if (user?.role === 'temsilci' || user?.role === 'abonelik') {
        rep = user.username; // Sadece kendi satışları
      } else {
        rep = repFilter === 'all' ? null : repFilter; // Admin tüm temsilcileri görebilir
      }
      
      const data = await getSales(days, rep);
      const sortedData = data.sort((a, b) => new Date(b.sale_date) - new Date(a.sale_date));
      setAllSales(sortedData); // Tüm satışları sakla
      setSales(sortedData); // Başlangıçta hepsini göster
    } catch (error) {
      toast.error('Satışlar yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const filterSales = () => {
    let filtered = allSales;
    
    // Tarih filtresi (yerel/TR saatine göre karşılaştır)
    if (startDate && endDate) {
      filtered = filtered.filter(sale => {
        const saleDate = getLocalDateString(new Date(sale.sale_date));
        return saleDate >= startDate && saleDate <= endDate;
      });
    }
    
    // Arama filtresi
    if (searchQuery.trim()) {
      filtered = filtered.filter(sale => 
        sale.customer_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        sale.product_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        sale.representative?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    // Kategori filtresi
    if (categoryFilter !== 'all') {
      filtered = filtered.filter(sale => {
        // Çoklu ürün satışları - items array'inde arama
        if (sale.items && sale.items.length > 0) {
          return sale.items.some(item => item.category === categoryFilter);
        }
        // Eski tek ürünlü satışlar
        return sale.category === categoryFilter;
      });
    }
    
    // Sıralama uygula
    filtered = filtered.sort((a, b) => {
      if (!sortConfig.key) return 0;
      
      let aValue, bValue;
      
      switch(sortConfig.key) {
        case 'sale_date':
          aValue = new Date(a.sale_date);
          bValue = new Date(b.sale_date);
          break;
        case 'customer_name':
          aValue = a.customer_name || '';
          bValue = b.customer_name || '';
          return sortConfig.direction === 'asc' 
            ? aValue.localeCompare(bValue, 'tr')
            : bValue.localeCompare(aValue, 'tr');
        case 'representative':
          aValue = a.representative || '';
          bValue = b.representative || '';
          return sortConfig.direction === 'asc' 
            ? aValue.localeCompare(bValue, 'tr')
            : bValue.localeCompare(aValue, 'tr');
        case 'quantity':
          aValue = a.quantity || 0;
          bValue = b.quantity || 0;
          break;
        case 'profit':
          aValue = a.profit || 0;
          bValue = b.profit || 0;
          break;
        case 'company_profit':
          aValue = a.company_profit || 0;
          bValue = b.company_profit || 0;
          break;
        default:
          return 0;
      }
      
      if (sortConfig.direction === 'asc') {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      } else {
        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
      }
    });
    
    setSales(filtered);
    setCurrentPage(1); // Arama/filtre değişince 1. sayfaya dön
  };

  const handleDeleteSale = async (saleId, customerName) => {
    if (!window.confirm(`"${customerName}" müşterisine yapılan satışı silmek istediğinizden emin misiniz?`)) {
      return;
    }

    try {
      await deleteSale(saleId);
      toast.success('Satış silindi');
      loadSales();
    } catch (error) {
      toast.error('Satış silinirken hata oluştu');
    }
  };

  const handleEditClick = (sale) => {
    setEditingSale(sale);
    const unitPrice = sale.quantity > 0 ? sale.price / sale.quantity : 0;
    setEditUnitPrice(unitPrice);
    setEditFormData({
      product_id: sale.product_id || null,
      product_name: sale.product_name,
      quantity: sale.quantity,
      camera_quantity: sale.camera_quantity || 0,
      price: sale.price || 0,
      notes: sale.notes || ''
    });
    setShowEditDialog(true);
  };

  // YENİ: Create dialog fonksiyonları
  const handleAddItem = () => {
    if (!currentItem.product_name || currentItem.quantity <= 0 || currentItem.unit_price <= 0) {
      toast.error('Lütfen tüm ürün bilgilerini doldurun');
      return;
    }

    const newItem = {
      ...currentItem,
      id: Date.now().toString()
    };

    setSaleItems([...saleItems, newItem]);
    setCurrentItem({
      product_id: '',
      product_name: '',
      category: 'Peşin Satış',
      quantity: 1,
      unit_price: 0,
      discount_percentage: 0
    });
    toast.success('Ürün eklendi');
  };

  const handleRemoveItem = (itemId) => {
    setSaleItems(saleItems.filter(item => item.id !== itemId));
    toast.success('Ürün kaldırıldı');
  };

  const handleCreateProductChange = (productId) => {
    const selectedProduct = products.find(p => p.id === productId);
    if (selectedProduct) {
      setCurrentItem({
        ...currentItem,
        product_id: selectedProduct.id,
        product_name: selectedProduct.name,
        category: selectedProduct.category || 'Peşin Satış',
        unit_price: selectedProduct.sale_price || 0
      });
    }
  };

  const calculateItemTotal = (item) => {
    const discountedPrice = item.unit_price * (1 - item.discount_percentage / 100);
    return discountedPrice * item.quantity;
  };

  const calculateGrandTotal = () => {
    return saleItems.reduce((sum, item) => sum + calculateItemTotal(item), 0);
  };

  const handleCreateSale = async () => {
    if (!createFormData.customer_id) {
      toast.error('Lütfen müşteri seçin');
      return;
    }

    if (saleItems.length === 0) {
      toast.error('Lütfen en az bir ürün ekleyin');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const API_BASE = process.env.REACT_APP_BACKEND_URL || window.location.origin;

      const saleData = {
        customer_id: createFormData.customer_id,
        items: saleItems.map(item => ({
          product_id: item.product_id,
          product_name: item.product_name,
          category: item.category,
          quantity: item.quantity,
          unit_price: item.unit_price,
          discount_percentage: item.discount_percentage
        })),
        notes: createFormData.notes,
        sale_date: combineDateWithCurrentTime(createFormData.sale_date)
      };

      const response = await fetch(`${API_BASE}/api/sales?token=${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(saleData)
      });

      if (!response.ok) {
        throw new Error('Satış eklenemedi');
      }

      toast.success('Satış başarıyla eklendi');
      setShowCreateDialog(false);
      setSaleItems([]);
      setCreateFormData({
        customer_id: '',
        notes: '',
        sale_date: getLocalDateString()
      });
      setCustomerSearch('');
      setShowCustomerDropdown(false);
      loadSales();
    } catch (error) {
      console.error('Create sale error:', error);
      toast.error('Satış eklenirken hata oluştu');
    }
  };

  const handleProductChange = (productId) => {
    const selectedProduct = products.find(p => p.id === productId);
    if (selectedProduct) {
      const unitPrice = selectedProduct.sale_price || 0;
      setEditUnitPrice(unitPrice);
      setEditFormData({
        ...editFormData,
        product_id: selectedProduct.id,
        product_name: selectedProduct.name,
        price: unitPrice * editFormData.quantity
      });
    }
  };

  const handleQuantityChange = (quantity) => {
    const qty = parseInt(quantity) || 1;
    const totalPrice = editUnitPrice * qty;
    setEditFormData({
      ...editFormData,
      quantity: qty,
      price: totalPrice
    });
  };

  const calculateProfit = () => {
    const selectedProduct = products.find(p => p.id === editFormData.product_id);
    if (selectedProduct && editFormData.price > 0) {
      const totalPurchaseCost = selectedProduct.purchase_price * editFormData.quantity;
      const totalSalePrice = editFormData.price;
      return (totalSalePrice - totalPurchaseCost).toFixed(2);
    }
    return '0.00';
  };

  const calculateTotalInvoice = () => {
    // Sadece toplam birim fiyatı göster (KDV hesaplama yok)
    const total = editFormData.price;
    return total.toFixed(2);
  };

  const handleUpdateSale = async () => {
    try {
      await updateSale(editingSale.id, {
        customer_id: editingSale.customer_id,
        product_id: editFormData.product_id,
        product_name: editFormData.product_name,
        quantity: editFormData.quantity,
        camera_quantity: editFormData.camera_quantity,
        price: editFormData.price,
        notes: editFormData.notes
      });
      toast.success('Satış güncellendi');
      setShowEditDialog(false);
      loadSales();
    } catch (error) {
      toast.error('Satış güncellenirken hata oluştu');
    }
  };

  const handleExportExcel = () => {
    try {
      // Filtre kontrolü - herhangi bir filtre uygulanmış mı?
      const isFiltered = searchQuery.trim() !== '' || 
                         categoryFilter !== 'all' || 
                         repFilter !== 'all' ||
                         startDate !== getLocalDateString(firstDayOfMonth) ||
                         endDate !== getLocalDateString(today);
      
      // Filtre varsa filtrelenmiş veriyi, yoksa tüm veriyi kullan
      const dataToExport = isFiltered ? sales : allSales;
      
      // Prepare data for export
      const exportData = dataToExport.map(sale => ({
        'Tarih': format(new Date(sale.sale_date), 'dd MMM yyyy HH:mm', { locale: tr }),
        'Müşteri': sale.customer_name,
        'Temsilci': sale.representative,
        'Ürün': sale.product_name,
        'Kategori': sale.items && sale.items.length > 0 
          ? Array.from(new Set(sale.items.map(item => item.category))).join(', ')
          : (sale.category || 'Diğer'),
        'Adet': sale.quantity,
        'Puan': sale.profit || 0,
        'Şirket Karı': sale.company_profit || 0,
        'Notlar': sale.notes || ''
      }));

      // Create worksheet
      const ws = XLSX.utils.json_to_sheet(exportData);
      
      // Set column widths
      ws['!cols'] = [
        { wch: 18 }, // Tarih
        { wch: 30 }, // Müşteri
        { wch: 20 }, // Temsilci
        { wch: 25 }, // Ürün
        { wch: 15 }, // Kategori
        { wch: 10 }, // Adet
        { wch: 12 }, // Puan
        { wch: 12 }, // Şirket Karı
        { wch: 40 }  // Notlar
      ];

      // Create workbook
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Satışlar');

      // Generate filename with current date
      const fileName = isFiltered 
        ? `ProGo_Satislar_Filtreli_${exportData.length}_adet.xlsx`
        : `ProGo_Tum_Satislar_${exportData.length}_adet.xlsx`;

      // Save file
      XLSX.writeFile(wb, fileName);
      
      toast.success(`${exportData.length} satış Excel'e aktarıldı${isFiltered ? ' (filtrelenmiş)' : ' (tümü)'}`);
    } catch (error) {
      toast.error('Excel dosyası oluşturulurken hata oluştu');
    }
  };

  if (loading) {
    return <div className="text-center py-12">Yükleniyor...</div>;
  }

  // Ürün kategorilerine göre istatistikler - ÜRÜN İSMİNE GÖRE
  const productStats = sales.reduce((stats, sale) => {
    // Çoklu ürün satışları için items array'ini kontrol et
    if (sale.items && sale.items.length > 0) {
      sale.items.forEach(item => {
        const productName = item.product_name || '';
        const category = item.category || '';
        const quantity = item.quantity || 0;
        
        // ARVENTO: Ürün ismine göre kontrol
        if (isArventoProduct(productName)) {
          stats.arvento += quantity;
        } else if (category === 'M2M') {
          stats.m2m += quantity;
        } else if (isCameraProduct(productName)) {
          // SADECE 3 dashcam ürünü Kamera olarak sayılır
          stats.camera += quantity;
        } else if (category === 'Kamera Ürünleri' || category === 'Sensörler') {
          // Diğer Kamera Ürünleri ve tüm Sensörler → Sensör
          stats.sensor += quantity;
        } else {
          stats.other += quantity;
        }
      });
    } else {
      // Tek ürünlü satışlar
      const productName = sale.product_name || '';
      const category = sale.category || '';
      const quantity = sale.quantity || 0;
      
      // ARVENTO: Ürün ismine göre kontrol
      if (isArventoProduct(productName)) {
        stats.arvento += quantity;
      } else if (category === 'M2M') {
        stats.m2m += quantity;
      } else if (isCameraProduct(productName)) {
        stats.camera += quantity;
      } else if (category === 'Kamera Ürünleri' || category === 'Sensörler') {
        stats.sensor += quantity;
      } else {
        stats.other += quantity;
      }
    }
    
    return stats;
  }, { arvento: 0, m2m: 0, camera: 0, sensor: 0, other: 0 });

  return (
    <div className="w-full overflow-x-hidden">
      <div className="space-y-4" data-testid="sales-page">
      <PageHeader
        icon={ShoppingCart}
        title="Satışlar"
        subtitle={`Toplam ${sales.length} satış | ${productStats.arvento} Arvento, ${productStats.m2m} M2M, ${productStats.camera} Kamera, ${productStats.sensor} Sensör${productStats.other > 0 ? `, ${productStats.other} Diğer` : ''}`}
      >
        <div className="flex flex-col sm:flex-row gap-2">
          <Button
            onClick={() => setShowCreateDialog(true)}
            className="bg-gradient-to-r from-green-600 to-green-700 text-white hover:from-green-700 hover:to-green-800 shadow-md text-sm md:text-base"
          >
            <ShoppingCart className="w-3 h-3 md:w-4 md:h-4 mr-2" />
            Yeni Satış
          </Button>
          <Button
            onClick={handleExportExcel}
            className="bg-white text-blue-600 hover:bg-blue-50 shadow-md text-sm md:text-base"
            data-testid="export-sales-button"
          >
            <Download className="w-3 h-3 md:w-4 md:h-4 mr-2" />
            Dışarı Aktar
          </Button>
        </div>
      </PageHeader>

      {/* Filters - TEK SATIR */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        {/* Üst Satır - Arama */}
        <div className="mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input
              type="text"
              placeholder="Müşteri, ürün veya temsilci ara..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 text-sm"
            />
          </div>
        </div>

        {/* Alt Satır - Filtreler */}
        <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-end gap-3">
          {/* Hızlı Tarih Butonları */}
          <div className="flex gap-2">
          <Button
            onClick={() => {
              const today = new Date();
              const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
              const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
              const formatDate = (date) => {
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                return `${year}-${month}-${day}`;
              };
              setStartDate(formatDate(lastMonth));
              setEndDate(formatDate(lastMonthEnd));
            }}
            variant="outline"
            size="sm"
            className="bg-gray-50 hover:bg-gray-100"
          >
            Geçen Ay
          </Button>
          <Button
            onClick={() => {
              const today = new Date();
              const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
              const formatDate = (date) => {
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                return `${year}-${month}-${day}`;
              };
              setStartDate(formatDate(firstDay));
              setEndDate(formatDate(today));
            }}
            variant="outline"
            size="sm"
            className="bg-green-50 hover:bg-green-100 flex-1 sm:flex-initial"
          >
            Bu Ay
          </Button>
          </div>
          
          {/* Tarih Seçiciler */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto">
            <div className="relative">
              <label className="absolute -top-2 left-2 bg-white px-1 text-xs text-gray-600">Başlangıç</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-full"
              />
            </div>
            <span className="text-gray-400 hidden sm:block">—</span>
            <div className="relative">
              <label className="absolute -top-2 left-2 bg-white px-1 text-xs text-gray-600">Bitiş</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-full"
              />
            </div>
          </div>

          {/* Temsilci Filtresi */}
          {/* Temsilci Filtresi - sadece admin görsün (değişmedi) */}
          {user?.role === 'admin' && (
            <Select value={repFilter} onValueChange={setRepFilter}>
              <SelectTrigger className="w-full sm:w-[200px]" data-testid="rep-filter-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Temsilciler</SelectItem>
                {users.map(u => (
                  <SelectItem key={u.id} value={u.username}>{u.full_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          
          {/* Kategori Filtresi */}
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-full sm:w-[200px]" data-testid="category-filter-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tüm Kategoriler</SelectItem>
              {CATEGORIES.map(cat => (
                <SelectItem key={cat} value={cat}>{cat}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Mobile Card View - ONLY MOBILE */}
      {isMobile && (
        <div className="space-y-1.5">
        {sales.length === 0 ? (
          <div className="bg-white rounded shadow p-4 text-center text-gray-500 text-xs">
            Henüz satış kaydı yok
          </div>
        ) : (
          sales
            .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
            .map((sale, index) => (
              <div 
                key={sale.id} 
                className="bg-white rounded shadow p-1 border-l-2 border-[#193046] hover:bg-gray-50"
                data-testid={`sale-card-${index}`}
              >
                <div className="mb-0.5">
                  <div className="flex items-center justify-between mb-0.5">
                    <span 
                      className="text-xs font-bold text-gray-900 truncate flex-1 mr-1 cursor-pointer hover:text-blue-600"
                      onClick={() => navigate(`/customers/${sale.customer_id}`)}
                    >
                      {sale.customer_name}
                    </span>
                    <span className="text-xs text-gray-600 flex-shrink-0">{format(new Date(sale.sale_date), 'dd/MM', { locale: tr })}</span>
                  </div>
                  <div className="text-xs text-gray-600">
                    {sale.items && sale.items.length > 0 ? (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setSelectedSaleItems(sale.items);
                          setShowItemsDialog(true);
                        }}
                        className="text-blue-600 hover:text-blue-800 font-medium"
                      >
                        {sale.items.map(item => item.product_name).join(', ')}
                      </button>
                    ) : (
                      <span className="truncate">{sale.product_name || '-'}</span>
                    )}
                  </div>
                </div>
                
                <div className="bg-gray-50 px-1 py-0.5 rounded mb-0.5">
                  <div className="flex items-center justify-between">
                    <div className="flex gap-1">
                      {sale.quantity > 0 && (
                        <span className="bg-green-100 text-green-800 px-1 py-0.5 rounded text-xs font-bold">
                          {sale.quantity}
                        </span>
                      )}
                      {sale.camera_quantity > 0 && (
                        <span className="bg-purple-100 text-purple-800 px-1 py-0.5 rounded text-xs font-bold">
                          {sale.camera_quantity}
                        </span>
                      )}
                    </div>
                    <div className="text-green-600 font-bold text-xs flex-shrink-0">
                      {sale.profit ? sale.profit.toFixed(0) : '0'} ₺
                    </div>
                  </div>
                  
                  {user?.role === 'admin' && sale.company_profit && (
                    <div className="flex items-center justify-between pt-0.5 border-t text-xs mt-0.5">
                      <span className="text-gray-600">Şirket:</span>
                      <span className="text-blue-600 font-bold">
                        {sale.company_profit.toFixed(0)} ₺
                      </span>
                    </div>
                  )}
                  
                  {sale.notes && (
                    <div className="pt-0.5 border-t text-xs text-gray-700 line-clamp-1 mt-0.5 break-words overflow-hidden">
                      {sale.notes}
                    </div>
                  )}
                </div>

                <div className="flex gap-0.5">
                  <Button 
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white h-6 text-xs px-1 py-0 min-w-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditClick(sale);
                    }}
                    data-testid={`edit-sale-${index}`}
                  >
                    Düzenle
                  </Button>
                  {/* Düzenleme/Silme butonları - admin, abonelik, temsilci yetkili */}
                  {['admin', 'abonelik', 'temsilci'].includes(user?.role) && (
                    <Button
                      variant="destructive"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteSale(sale.id, sale.customer_name);
                      }}
                      className="h-6 px-1.5 py-0 flex-shrink-0"
                      data-testid={`delete-sale-${index}`}
                    >
                      <Trash2 className="w-2.5 h-2.5" />
                    </Button>
                  )}
                </div>
              </div>
            ))
        )}
      </div>
      )}

      {/* Desktop Table View - ONLY DESKTOP */}
      {!isMobile && (
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="progo-gradient text-gray-800">
              <tr>
                <th className="px-3 py-3 text-left text-xs cursor-pointer hover:bg-gray-100" onClick={() => {
                  setSortConfig({
                    key: 'sale_date',
                    direction: sortConfig.key === 'sale_date' && sortConfig.direction === 'asc' ? 'desc' : 'asc'
                  });
                }}>
                  Tarih {sortConfig.key === 'sale_date' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </th>
                <th className="px-3 py-3 text-left text-xs cursor-pointer hover:bg-gray-100" onClick={() => {
                  setSortConfig({
                    key: 'customer_name',
                    direction: sortConfig.key === 'customer_name' && sortConfig.direction === 'asc' ? 'desc' : 'asc'
                  });
                }}>
                  Müşteri {sortConfig.key === 'customer_name' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </th>
                <th className="px-3 py-3 text-left text-xs cursor-pointer hover:bg-gray-100" onClick={() => {
                  setSortConfig({
                    key: 'representative',
                    direction: sortConfig.key === 'representative' && sortConfig.direction === 'asc' ? 'desc' : 'asc'
                  });
                }}>
                  Temsilci {sortConfig.key === 'representative' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </th>
                <th className="px-3 py-3 text-left text-xs">Ürün</th>
                <th className="px-2 py-3 text-center text-xs cursor-pointer hover:bg-gray-100" onClick={() => {
                  setSortConfig({
                    key: 'quantity',
                    direction: sortConfig.key === 'quantity' && sortConfig.direction === 'asc' ? 'desc' : 'asc'
                  });
                }}>
                  Adet {sortConfig.key === 'quantity' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </th>
                <th className="px-2 py-3 text-left text-xs">Ürün Grubu</th>
                <th className="px-2 py-3 text-center text-xs cursor-pointer hover:bg-gray-100" onClick={() => {
                  setSortConfig({
                    key: 'profit',
                    direction: sortConfig.key === 'profit' && sortConfig.direction === 'asc' ? 'desc' : 'asc'
                  });
                }}>
                  Puan {sortConfig.key === 'profit' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </th>
                {/* Şirket karı kolonu - sadece admin */}
                {user?.role === 'admin' && (
                  <th className="px-2 py-3 text-center text-xs cursor-pointer hover:bg-gray-100" onClick={() => {
                    setSortConfig({
                      key: 'company_profit',
                      direction: sortConfig.key === 'company_profit' && sortConfig.direction === 'asc' ? 'desc' : 'asc'
                    });
                  }}>
                    Şirket Karı {sortConfig.key === 'company_profit' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                  </th>
                )}
                <th className="px-3 py-3 text-left text-xs">Notlar</th>
                {/* İşlemler kolonu - admin, abonelik, temsilci için */}
                {['admin', 'abonelik', 'temsilci'].includes(user?.role) && (
                  <th className="px-2 py-3 text-center text-xs">İşlemler</th>
                )}
              </tr>
            </thead>
            <tbody>
              {sales.length === 0 ? (
                <tr>
                  <td colSpan={user?.role === 'admin' ? "10" : ['abonelik', 'temsilci'].includes(user?.role) ? "9" : "8"} className="px-6 py-12 text-center text-gray-500">
                    Henüz satış kaydı yok
                  </td>
                </tr>
              ) : (
                sales
                  .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                  .map((sale, index) => (
                  <tr 
                    key={sale.id} 
                    className="border-b hover:bg-gray-50 transition-colors" 
                    data-testid={`sale-row-${index}`}
                  >
                    <td className="px-2 py-2 text-xs">
                      <div className="flex items-center gap-1">
                        <Calendar className="w-3 h-3 text-gray-400" />
                        <span className="whitespace-nowrap">{format(new Date(sale.sale_date), 'dd MMM HH:mm', { locale: tr })}</span>
                      </div>
                    </td>
                    <td 
                      className="px-2 py-2 font-medium text-xs text-gray-900 cursor-pointer hover:text-blue-600"
                      onClick={() => navigate(`/customers/${sale.customer_id}`)}
                    >
                      <div className="max-w-[150px] truncate">{sale.customer_name}</div>
                    </td>
                    <td className="px-2 py-2 text-xs">
                      <div className="max-w-[100px] truncate">{sale.representative}</div>
                    </td>
                    <td className="px-2 py-2 text-xs">
                      {sale.items && sale.items.length > 0 ? (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setSelectedSaleItems(sale.items);
                            setShowItemsDialog(true);
                          }}
                          className="text-blue-600 hover:text-blue-800 font-medium"
                        >
                          {sale.items.map(item => item.product_name).join(', ')}
                        </button>
                      ) : (
                        <div className="max-w-[120px] truncate">{sale.product_name || '-'}</div>
                      )}
                    </td>
                    <td className="px-2 py-2 text-center">
                      {sale.quantity > 0 && (
                        <span className="bg-green-100 text-green-800 px-1.5 py-0.5 rounded-full text-xs font-medium">
                          {sale.quantity}
                        </span>
                      )}
                      {sale.camera_quantity > 0 && (
                        <span className="bg-purple-100 text-purple-800 px-1.5 py-0.5 rounded-full text-xs font-medium ml-1">
                          {sale.camera_quantity}
                        </span>
                      )}
                    </td>
                    <td className="px-2 py-2 text-xs">
                      <div className="max-w-[80px] truncate text-gray-700">
                        {sale.items && sale.items.length > 0 
                          ? Array.from(new Set(sale.items.map(item => item.category))).join(', ')
                          : (sale.category || 'Diğer')
                        }
                      </div>
                    </td>
                    <td className="px-2 py-2 text-center text-xs">
                      <span className="text-green-600 font-semibold whitespace-nowrap">
                        {sale.profit ? sale.profit.toFixed(0) : '0'} ₺
                      </span>
                    </td>
                    {/* Şirket karı kolonu - sadece admin görsün (değişmedi) */}
                    {user?.role === 'admin' && (
                      <td className="px-2 py-2 text-center text-xs">
                        <span className="text-blue-600 font-semibold whitespace-nowrap">
                          {sale.company_profit ? sale.company_profit.toFixed(0) : '0'} ₺
                        </span>
                      </td>
                    )}
                    <td className="px-2 py-2 text-gray-600 text-xs max-w-[120px] truncate">{sale.notes || '-'}</td>
                    <td className="px-2 py-2 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditClick(sale);
                          }}
                          data-testid={`edit-sale-${index}`}
                          className="text-xs px-1.5 py-1 h-7"
                        >
                          <Edit className="w-3 h-3" />
                        </Button>
                        {/* Düzenleme/Silme butonları - admin, abonelik, temsilci yetkili */}
                        {['admin', 'abonelik', 'temsilci'].includes(user?.role) && (
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteSale(sale.id, sale.customer_name);
                            }}
                            data-testid={`delete-sale-${index}`}
                            className="text-xs px-1.5 py-1 h-7"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            <tfoot className="bg-blue-50 border-t-2 border-blue-300">
              <tr className="font-bold">
                <td className="px-3 py-3 text-xs text-gray-900">TOPLAM</td>
                <td className="px-3 py-3 text-xs text-center text-gray-900" colSpan="3">
                  {sales.length} Satış
                </td>
                <td className="px-2 py-3 text-xs text-center text-gray-900">
                  {sales.reduce((sum, sale) => sum + (sale.quantity || 0), 0)}
                </td>
                <td className="px-2 py-3 text-xs"></td>
                <td className="px-2 py-3 text-xs text-center text-blue-900">
                  ₺{sales.reduce((sum, sale) => sum + (sale.profit || 0), 0).toLocaleString('tr-TR')}
                </td>
                {user?.role === 'admin' && (
                  <td className="px-2 py-3 text-xs text-center text-gray-900">
                    ₺{sales.reduce((sum, sale) => sum + (sale.company_profit || 0), 0).toLocaleString('tr-TR')}
                  </td>
                )}
                <td className="px-3 py-3 text-xs"></td>
                <td className="px-2 py-3 text-xs"></td>
              </tr>
            </tfoot>
          </table>
        </div>

        {/* Pagination */}
        {sales.length > itemsPerPage && (
          <div className="flex items-center justify-between px-4 py-3 border-t">
            <div className="text-sm text-gray-600">
              Toplam {sales.length} kayıttan {Math.min((currentPage - 1) * itemsPerPage + 1, sales.length)}-{Math.min(currentPage * itemsPerPage, sales.length)} arası gösteriliyor
            </div>
            <div className="flex space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
              >
                Önceki
              </Button>
              <div className="flex items-center space-x-1">
                {Array.from({ length: Math.ceil(sales.length / itemsPerPage) }, (_, i) => i + 1)
                  .filter(page => {
                    const totalPages = Math.ceil(sales.length / itemsPerPage);
                    return page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1;
                  })
                  .map((page, idx, array) => (
                    <React.Fragment key={page}>
                      {idx > 0 && array[idx - 1] !== page - 1 && (
                        <span className="px-2 text-gray-400">...</span>
                      )}
                      <Button
                        variant={currentPage === page ? "default" : "outline"}
                        size="sm"
                        onClick={() => setCurrentPage(page)}
                        className={currentPage === page ? "progo-bg" : ""}
                      >
                        {page}
                      </Button>
                    </React.Fragment>
                  ))}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(prev => Math.min(Math.ceil(sales.length / itemsPerPage), prev + 1))}
                disabled={currentPage === Math.ceil(sales.length / itemsPerPage)}
              >
                Sonraki
              </Button>
            </div>
          </div>
        )}
      </div>
      )}

      {/* Mobile Pagination */}
      {sales.length > itemsPerPage && (
        <div className="md:hidden flex flex-col sm:flex-row justify-between items-center gap-4 bg-white p-4 rounded-lg shadow">
          <div className="text-sm text-gray-700">
            Gösterilen: {(currentPage - 1) * itemsPerPage + 1} - {Math.min(currentPage * itemsPerPage, sales.length)} / {sales.length}
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
              variant="outline"
              size="sm"
              className="min-h-10"
            >
              Önceki
            </Button>
            <span className="px-4 py-2 text-sm font-medium">
              {currentPage} / {Math.ceil(sales.length / itemsPerPage)}
            </span>
            <Button
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === Math.ceil(sales.length / itemsPerPage)}
              variant="outline"
              size="sm"
              className="min-h-10"
            >
              Sonraki
            </Button>
          </div>
        </div>
      )}

      {/* Create Sale Dialog - Çoklu Ürün */}
      <Dialog open={showCreateDialog} onOpenChange={(open) => {
        setShowCreateDialog(open);
        if (open) {
          // Dialog açıldığında formu sıfırla
          setCreateFormData({
            customer_id: '',
            notes: '',
            sale_date: getLocalDateString()
          });
          setSaleItems([]);
          setCurrentItem({
            product_id: '',
            product_name: '',
            category: 'Peşin Satış',
            quantity: 1,
            unit_price: 0,
            discount_percentage: 0
          });
          setCustomerSearch('');
          setShowCustomerDropdown(false);
        }
      }}>
        <DialogContent className="max-w-4xl max-h-[95vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Yeni Satış Ekle</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Müşteri Seçimi - Gelişmiş Arama */}
            <div className="relative customer-search-container">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Müşteri *</label>
              <Input
                placeholder="Müşteri ara (ad, telefon)..."
                value={customerSearch}
                onChange={(e) => {
                  setCustomerSearch(e.target.value);
                  if (e.target.value.length > 0) {
                    setShowCustomerDropdown(true);
                  }
                }}
                onFocus={() => {
                  if (customerSearch.length > 0 && !createFormData.customer_id) {
                    setShowCustomerDropdown(true);
                  }
                }}
              />
              
              {/* Seçili Müşteri */}
              {createFormData.customer_id && !showCustomerDropdown && (
                <div className="mt-2 p-2 bg-blue-50 rounded border border-blue-200 flex justify-between items-center">
                  <span className="text-sm font-medium">
                    {customers.find(c => c.id === createFormData.customer_id)?.name}
                  </span>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setCreateFormData({ ...createFormData, customer_id: '' });
                      setCustomerSearch('');
                      setShowCustomerDropdown(true);
                    }}
                  >
                    Değiştir
                  </Button>
                </div>
              )}
              
              {/* Dropdown Listesi */}
              {showCustomerDropdown && (
                <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {(() => {
                    const query = customerSearch.toLowerCase();
                    // Önce baştan başlayanları, sonra kelime başlarında olanları göster
                    const startsWithResults = customers.filter(customer => 
                      customer.name.toLowerCase().startsWith(query) ||
                      customer.phone?.startsWith(customerSearch)
                    );
                    const wordStartResults = customers.filter(customer => 
                      !customer.name.toLowerCase().startsWith(query) &&
                      customer.name.toLowerCase().split(' ').some(word => word.startsWith(query))
                    );
                    const results = [...startsWithResults, ...wordStartResults].slice(0, 50);
                    
                    if (results.length === 0) {
                      return (
                        <div className="p-3 text-center text-gray-500">
                          Müşteri bulunamadı
                        </div>
                      );
                    }
                    
                    return results.map(customer => (
                      <div
                        key={customer.id}
                        className="p-3 hover:bg-gray-100 cursor-pointer border-b last:border-b-0"
                        onClick={() => {
                          setCreateFormData({ ...createFormData, customer_id: customer.id });
                          setCustomerSearch(customer.name);
                          setShowCustomerDropdown(false);
                        }}
                      >
                        <div className="font-medium">{customer.name}</div>
                        {customer.phone && (
                          <div className="text-xs text-gray-500">{customer.phone}</div>
                        )}
                        {customer.representative && (
                          <div className="text-xs text-gray-400">Temsilci: {customer.representative}</div>
                        )}
                      </div>
                    ));
                  })()}
                </div>
              )}
            </div>

            {/* Satış Tarihi */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Satış Tarihi</label>
              <Input
                type="date"
                value={createFormData.sale_date}
                onChange={(e) => setCreateFormData({...createFormData, sale_date: e.target.value})}
              />
            </div>

            {/* Ürün Ekleme Formu */}
            <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
              <h3 className="text-sm font-bold text-gray-800 mb-3">Ürün Ekle</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {/* Ürün Seçimi */}
                <div className="md:col-span-2">
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">Ürün *</label>
                  <Select value={currentItem.product_id} onValueChange={handleCreateProductChange}>
                    <SelectTrigger>
                      <SelectValue placeholder="Ürün seçin..." />
                    </SelectTrigger>
                    <SelectContent>
                      {products.filter(p => p.for_sales).map(product => (
                        <SelectItem key={product.id} value={product.id}>
                          {product.name} - {product.category} ({product.sale_price} ₺)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Kategori */}
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">Kategori</label>
                  <Input
                    value={currentItem.category}
                    onChange={(e) => setCurrentItem({...currentItem, category: e.target.value})}
                    readOnly
                    className="bg-gray-100"
                  />
                </div>

                {/* Adet */}
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">Adet *</label>
                  <Input
                    type="number"
                    min="1"
                    value={currentItem.quantity}
                    onChange={(e) => setCurrentItem({...currentItem, quantity: parseInt(e.target.value) || 1})}
                  />
                </div>

                {/* Birim Fiyat */}
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">Birim Fiyat (₺) *</label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={currentItem.unit_price}
                    onChange={(e) => setCurrentItem({...currentItem, unit_price: parseFloat(e.target.value) || 0})}
                  />
                </div>

                {/* İndirim */}
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">İndirim (%)</label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={currentItem.discount_percentage}
                    onChange={(e) => setCurrentItem({...currentItem, discount_percentage: parseFloat(e.target.value) || 0})}
                  />
                </div>
              </div>

              <Button
                onClick={handleAddItem}
                className="w-full mt-3 bg-blue-600 hover:bg-blue-700 text-white"
                type="button"
              >
                <Plus className="w-4 h-4 mr-2" />
                Ürün Ekle
              </Button>
            </div>

            {/* Eklenen Ürünler Listesi */}
            {saleItems.length > 0 && (
              <div className="border border-gray-200 rounded-lg p-4">
                <h3 className="text-sm font-bold text-gray-800 mb-3">Eklenen Ürünler ({saleItems.length})</h3>
                <div className="space-y-2">
                  {saleItems.map((item, index) => {
                    const discountedPrice = item.unit_price * (1 - item.discount_percentage / 100);
                    const itemTotal = discountedPrice * item.quantity;
                    
                    return (
                      <div key={item.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-gray-900">{index + 1}.</span>
                            <span className="font-medium text-gray-800">{item.product_name}</span>
                            <span className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded-full">{item.category}</span>
                          </div>
                          <div className="text-sm text-gray-600 mt-1">
                            {item.quantity} adet × {item.unit_price.toFixed(2)} ₺
                            {item.discount_percentage > 0 && (
                              <span className="text-green-600 ml-2">
                                (-%{item.discount_percentage} = {discountedPrice.toFixed(2)} ₺)
                              </span>
                            )}
                            <span className="font-semibold ml-2">= {itemTotal.toFixed(2)} ₺</span>
                          </div>
                        </div>
                        <Button
                          onClick={() => handleRemoveItem(item.id)}
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    );
                  })}
                </div>

                {/* Genel Toplam */}
                <div className="mt-4 pt-4 border-t border-gray-300">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-bold text-gray-900">GENEL TOPLAM:</span>
                    <span className="text-2xl font-bold text-green-600">{calculateGrandTotal().toFixed(2)} ₺</span>
                  </div>
                </div>
              </div>
            )}

            {/* Notlar */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Notlar</label>
              <textarea
                value={createFormData.notes}
                onChange={(e) => setCreateFormData({...createFormData, notes: e.target.value})}
                className="w-full border border-gray-300 rounded-lg p-2 text-sm"
                rows={3}
                placeholder="Satış notları..."
              />
            </div>

            {/* Kaydet Butonu */}
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button
                onClick={() => {
                  setShowCreateDialog(false);
                  setSaleItems([]);
                  setCreateFormData({ customer_id: '', notes: '', sale_date: getLocalDateString() });
                }}
                variant="outline"
              >
                İptal
              </Button>
              <Button
                onClick={handleCreateSale}
                className="bg-green-600 hover:bg-green-700 text-white"
                disabled={saleItems.length === 0 || !createFormData.customer_id}
              >
                Satışı Kaydet
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Sale Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-full sm:max-w-2xl max-h-[95vh] overflow-y-auto mx-2 sm:mx-4">
          <DialogHeader>
            <DialogTitle>Satış Düzenle</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Ürün</Label>
              <Select 
                value={editFormData.product_id || undefined} 
                onValueChange={handleProductChange}
              >
                <SelectTrigger data-testid="edit-sale-product-select">
                  <SelectValue placeholder="Ürün seçiniz" />
                </SelectTrigger>
                <SelectContent className="max-h-[400px]">
                  {/* Group products by category */}
                  {Object.entries(
                    products.reduce((acc, product) => {
                      const category = product.category || 'Diğer';
                      // Kategori ismini değiştir
                      let displayCategory = category;
                      if (category === "Peşin Satış") {
                        displayCategory = "Peşin Satış Adedi";
                      } else if (category === "Arvento Taksitli") {
                        displayCategory = "Arvento Taksitli Satışlar";
                      }
                      if (!acc[displayCategory]) acc[displayCategory] = [];
                      acc[displayCategory].push(product);
                      return acc;
                    }, {})
                  ).map(([category, categoryProducts]) => (
                    <div key={category}>
                      <div className="px-2 py-2 text-xs font-semibold text-gray-500 bg-gray-100 sticky top-0">
                        {category} ({categoryProducts.length})
                      </div>
                      {categoryProducts.map(product => (
                        <SelectItem key={product.id} value={product.id} className="pl-6">
                          {product.name}
                        </SelectItem>
                      ))}
                    </div>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Adet</Label>
              <Input
                type="number"
                value={editFormData.quantity}
                onChange={(e) => handleQuantityChange(e.target.value)}
                min="1"
                data-testid="edit-sale-quantity"
              />
            </div>

            <div>
              <Label>Birim Satış Fiyatı (TL)</Label>
              <Input
                type="number"
                step="0.01"
                value={editUnitPrice}
                onChange={(e) => {
                  const unitPrice = parseFloat(e.target.value) || 0;
                  setEditUnitPrice(unitPrice);
                  const totalPrice = unitPrice * editFormData.quantity;
                  setEditFormData({ ...editFormData, price: totalPrice });
                }}
                min="0"
                data-testid="edit-sale-unit-price"
              />
            </div>

            <div className="bg-blue-50 p-4 rounded-lg space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-700 font-medium">Toplam Fiyat:</span>
                <span className="text-blue-600 font-bold text-lg">
                  {calculateTotalInvoice()} TL
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-700 font-medium">Puan:</span>
                <span className="text-green-600 font-bold text-lg">
                  {calculateProfit()} TL
                </span>
              </div>
            </div>

            <div>
              <Label>Notlar</Label>
              <Textarea
                value={editFormData.notes}
                onChange={(e) => setEditFormData({ ...editFormData, notes: e.target.value })}
                rows={3}
                data-testid="edit-sale-notes"
              />
            </div>
            <div className="flex justify-end space-x-3">
              <Button variant="outline" onClick={() => setShowEditDialog(false)}>İptal</Button>
              <Button onClick={handleUpdateSale} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="save-sale-edit">
                Kaydet
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Ürün Detayları Modal */}
      <Dialog open={showItemsDialog} onOpenChange={setShowItemsDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Satış Ürün Detayları</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {selectedSaleItems && selectedSaleItems.length > 0 ? (
              <>
                <div className="rounded-lg border border-gray-200 overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">#</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Ürün Adı</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Kategori</th>
                        <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">Adet</th>
                        <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">Birim Fiyat</th>
                        <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">İndirim</th>
                        <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">Toplam</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedSaleItems.map((item, index) => {
                        const discountedPrice = item.unit_price * (1 - (item.discount_percentage || 0) / 100);
                        const itemTotal = discountedPrice * item.quantity;
                        
                        return (
                          <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm text-gray-600">{index + 1}</td>
                            <td className="px-4 py-3 text-sm font-medium text-gray-900">{item.product_name}</td>
                            <td className="px-4 py-3 text-sm text-gray-700">
                              <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">
                                {item.category}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-center text-sm font-semibold text-gray-900">{item.quantity}</td>
                            <td className="px-4 py-3 text-right text-sm text-gray-700">{item.unit_price.toFixed(2)} ₺</td>
                            <td className="px-4 py-3 text-center text-sm text-gray-700">
                              {item.discount_percentage > 0 ? `%${item.discount_percentage}` : '-'}
                            </td>
                            <td className="px-4 py-3 text-right text-sm font-semibold text-green-600">
                              {itemTotal.toFixed(2)} ₺
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot className="bg-gray-50">
                      <tr>
                        <td colSpan="6" className="px-4 py-4 text-right text-base font-bold text-gray-900">
                          GENEL TOPLAM:
                        </td>
                        <td className="px-4 py-4 text-right text-lg font-bold text-green-600">
                          {selectedSaleItems.reduce((sum, item) => {
                            const discountedPrice = item.unit_price * (1 - (item.discount_percentage || 0) / 100);
                            return sum + (discountedPrice * item.quantity);
                          }, 0).toFixed(2)} ₺
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
                
                <div className="flex justify-end">
                  <Button
                    onClick={() => setShowItemsDialog(false)}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    Kapat
                  </Button>
                </div>
              </>
            ) : (
              <div className="text-center py-8 text-gray-500">
                Ürün detayı bulunamadı.
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
      </div>
    </div>
  );
};

export default Sales;